import area from "@turf/area";
import { STYLES } from "./polygonStyles";

/* ------------------------- preview layers ------------------------- */

const DRAW_SRC = "draw-preview";
const DRAW_COLOR = STYLES.sim.lineColor; // same color as the simulated polygon
const EMPTY = { type: "FeatureCollection", features: [] };

const ensureDrawLayers = (map) => {
  if (!map.isStyleLoaded()) return false;
  if (!map.getSource(DRAW_SRC)) {
    map.addSource(DRAW_SRC, { type: "geojson", data: EMPTY });
    map.addLayer({
      id: `${DRAW_SRC}-fill`,
      type: "fill",
      source: DRAW_SRC,
      filter: ["==", "$type", "Polygon"],
      paint: { "fill-color": DRAW_COLOR, "fill-opacity": 0.12 },
    });
    map.addLayer({
      id: `${DRAW_SRC}-line`,
      type: "line",
      source: DRAW_SRC,
      filter: ["==", "$type", "LineString"],
      paint: {
        "line-color": DRAW_COLOR,
        "line-width": 2,
        "line-dasharray": [2, 2],
      },
    });
    map.addLayer({
      id: `${DRAW_SRC}-pts`,
      type: "circle",
      source: DRAW_SRC,
      filter: ["==", "$type", "Point"],
      paint: {
        "circle-radius": 5,
        "circle-color": "#ffffff",
        "circle-stroke-color": DRAW_COLOR,
        "circle-stroke-width": 2,
      },
    });
  }
  return true;
};

const clearDrawPreview = (map) => {
  try {
    [`${DRAW_SRC}-fill`, `${DRAW_SRC}-line`, `${DRAW_SRC}-pts`].forEach(
      (l) => map.getLayer(l) && map.removeLayer(l),
    );
    if (map.getSource(DRAW_SRC)) map.removeSource(DRAW_SRC);
  } catch {
    /* map already removed */
  }
};

const renderDrawPreview = (map, verts, cursor) => {
  if (!ensureDrawLayers(map)) return;
  const point = (c) => ({
    type: "Feature",
    properties: {},
    geometry: { type: "Point", coordinates: c },
  });
  const features = verts.map(point);
  const path = cursor ? [...verts, cursor] : verts;

  if (path.length >= 2) {
    features.push({
      type: "Feature",
      properties: {},
      geometry: { type: "LineString", coordinates: path },
    });
  }
  if (path.length >= 3) {
    features.push({
      type: "Feature",
      properties: {},
      geometry: { type: "Polygon", coordinates: [[...path, path[0]]] },
    });
  }
  map.getSource(DRAW_SRC).setData({ type: "FeatureCollection", features });
};

/* --------------------------- draw session --------------------------- */

/**
 * Starts a drawing session on the map.
 * click = add a vertex, double-click or Enter = close, Escape = cancel.
 * Returns { cancel } which stops the session silently.
 */
export const startPolygonDraw = (map, { onProgress, onComplete, onCancel }) => {
  const verts = [];
  let cursor = null;
  const canvas = map.getCanvas();
  const previousCursor = canvas.style.cursor;

  canvas.style.cursor = "crosshair";
  map.doubleClickZoom.disable();

  const cleanup = () => {
    map.off("click", onClick);
    map.off("mousemove", onMove);
    map.off("dblclick", onDblClick);
    document.removeEventListener("keydown", onKey);
    clearDrawPreview(map);
    canvas.style.cursor = previousCursor;
    // deferred so the dblclick that just fired does not zoom the map
    setTimeout(() => map.doubleClickZoom?.enable(), 0);
  };

  const finish = () => {
    if (verts.length < 3) return;
    const feature = {
      type: "Feature",
      properties: {},
      geometry: { type: "Polygon", coordinates: [[...verts, verts[0]]] },
    };
    const result = {
      feature,
      area: Math.round((area(feature) / 10000) * 10) / 10, // hectares
      vertices: verts.length,
    };
    cleanup();
    onComplete?.(result);
  };

  function onClick(e) {
    verts.push([e.lngLat.lng, e.lngLat.lat]);
    renderDrawPreview(map, verts, cursor);
    onProgress?.(verts.length);
  }

  function onMove(e) {
    cursor = [e.lngLat.lng, e.lngLat.lat];
    if (verts.length) renderDrawPreview(map, verts, cursor);
  }

  function onDblClick(e) {
    e.preventDefault();
    verts.pop(); // the 2nd click of a double-click duplicated the last vertex
    onProgress?.(verts.length);
    renderDrawPreview(map, verts, cursor);
    finish();
  }

  function onKey(e) {
    if (e.key === "Escape") {
      cleanup();
      onCancel?.();
    } else if (e.key === "Enter") {
      finish();
    }
  }

  map.on("click", onClick);
  map.on("mousemove", onMove);
  map.on("dblclick", onDblClick);
  document.addEventListener("keydown", onKey);

  return { cancel: cleanup };
};
